import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import type { IconSize } from '@databox/icons/dist/types';

import type { DSSetup, Service } from 'webapp-types';
import type { Theme } from 'webapp/enums/enums';
import { Direction, PopupEvent } from 'webapp/enums/enums';
import { equal } from 'webapp/utils/common';
import { sanitize } from 'webapp/utils/html';

import themed from 'webapp/decorators/themed';
import joinClasses from 'webapp/helpers/join-classes';
import CommonMasterFilterPopupComponent from 'webapp/components/common/master-filter/popup';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import ToolTipComponent from 'webapp/components/tool-tip';

import MetricSetup from 'webapp/models-custom/metric-setup';
import MetricSetupForMasterFilter from 'webapp/models-custom/metric-setup-for-master-filter';

export interface Args {
	save: {
		// dimensions null means reset
		(dimensions: null): void;
		(dimensions: string[] | undefined, filters?: DSSetup.MetricSetupFiltersData | null): void;
	};
	theme?: Theme | string;
	isDisabled?: boolean;
	iconSize?: IconSize;
}

@themed()
abstract class CommonMasterFilterIndexComponent<T extends Args> extends Component<T> {
	@service('account') declare accountService: Service.Account;
	@service('popup') declare popupService: Service.Popup;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;

	declare theme: string; // from @themed
	declare themeClass: string; // from @themed

	// the ones saved on the board/block state
	abstract get dimensions(): string[] | undefined; // undefined means not set/changed
	abstract get filters(): DSSetup.MetricSetupFiltersData | null | undefined; // undefined means not set, null means explicitly empty

	abstract get metricDataItems(): DSSetup.MetricData[];
	abstract get allowAdvancedSetup(): boolean;
	abstract get allowAdvancedFilters(): boolean;
	abstract get metricSetupDataItems(): DSSetup.MetricSetupData[];
	abstract origin: string;

	@tracked isPopupOpen = false;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		if (this.navigationService.isMainApp) {
			// need them to show dimensions in the popup
			void this.mmMetricsService.requestMetrics();
		}
	}

	@cached
	get metricSetup() {
		const selectedDimensions = this.selectedDimensions;

		const metricSetup = new MetricSetupForMasterFilter({
			dimensions: selectedDimensions || [],
			filters: this.selectedFilters,
		});

		metricSetup._metricData = this.metricDataItems;

		// committed [] = "no breakdown" (specific); null = mixed (unspecific)
		metricSetup.isUnspecificDimensionSelected = selectedDimensions === null;

		return metricSetup;
	}

	get selectedDimensions(): string[] | null {
		// committed dimensions on the board/block state, or the common dimensions across metrics if not committed.
		// null means mixed (no shared state); [] means "no breakdown" was explicitly committed.
		const dimensions = this.dimensions;
		return dimensions ?? this.getCommonDimensions();
	}

	get selectedFilters() {
		if (this.filters === undefined) {
			return this.getCommonFilters();
		} else {
			return this.filters;
		}
	}

	get hasChanges() {
		return !!this.dimensions || this.filters !== undefined;
	}

	get hasAnyMetrics() {
		return this.metricDataItems.length > 0;
	}

	get preferredPopupDirections() {
		return [Direction.DOWN, Direction.UP];
	}

	get iconSize(): IconSize {
		return this.args.iconSize ?? 'sm';
	}

	@action
	async openPopup(e: PointerEvent) {
		if (this.accountService.handleDemo()) {
			return;
		}

		if (this.isPopupOpen) {
			return;
		}

		if (this.args.isDisabled) {
			return;
		}

		const attachTo = (e.target as HTMLElement)?.closest<HTMLElement>('.filter-selector');

		if (!attachTo) {
			return;
		}

		const openAsModal =
			this.allowAdvancedFilters && this.metricSetup.dimensionFilterItemsCount > 1;

		const metricSetupCopy = this.metricSetup.createCopy();

		if (openAsModal) {
			// the context popup loads the dimensions itself, the modal doesn't
			await this.mmMetricsService.loadMasterFilterDimensions(metricSetupCopy).catch(() => {
				// open it anyway, as the context popup does
			});
		}

		let popup;

		if (openAsModal) {
			popup = this.mmMetricsService.openFiltersPopup(
				{
					metricSetup: metricSetupCopy,
					apply: (
						dimensions: string[] | undefined,
						filters: DSSetup.MetricSetupFiltersData | null,
					) => {
						this.args.save(dimensions, filters);
					},
					clear: this.handleReset,
					allowMultipleDimensions: this.allowAdvancedSetup,
				},
				this.theme,
			);
		} else {
			popup = this.popupService.createContext(CommonMasterFilterPopupComponent, attachTo, {
				popupClass: 'popup-type-master-filter',
				directions: this.preferredPopupDirections,
				theme: this.theme,
			});

			popup.open({
				metricSetup: metricSetupCopy,
				allowAdvancedSetup: this.allowAdvancedSetup,
				allowAdvancedFilters: this.allowAdvancedFilters,
				origin: this.origin,
				save: (
					dimensions: string[] | undefined,
					filters: DSSetup.MetricSetupFiltersData | null,
				) => {
					this.args.save(dimensions, filters);
				},
				reset: this.handleReset,
			});
		}

		popup.on(PopupEvent.OPENED, this, () => {
			this.isPopupOpen = true;
			this.onIsPopupOpenChange();
		});

		popup.on(PopupEvent.CLOSING, this, () => {
			this.isPopupOpen = false;
			this.onIsPopupOpenChange();
		});
	}

	@action
	handleReset() {
		// if there are changes on the preview, remove them
		// if the filters seen are from the "common" filters, save empty
		if (this.hasChanges) {
			this.args.save(null);
		} else {
			this.args.save([], null);
		}
	}

	protected onIsPopupOpenChange() {
		// possibly extended
	}

	private getCommonDimensions(): string[] | null {
		// returns the shared dimensions array across all metric-setup-data items, or null if mixed
		const items = this.metricSetupDataItems;
		if (!items.length) {
			return [];
		}
		const first = items[0]!.dimensions ?? [];
		const firstSorted = [...first].sort();
		for (let i = 1; i < items.length; i++) {
			const current = [...(items[i]!.dimensions ?? [])].sort();
			if (!equal(current, firstSorted)) {
				return null;
			}
		}
		return first;
	}

	private getCommonFilters() {
		let hasCommon = true;

		let metricSetup: MetricSetup | undefined;

		this.metricSetupDataItems.forEach((msd) => {
			const metricSetupTmp = new MetricSetup(msd);
			if (metricSetup) {
				if (!metricSetup.isMatchingFiltersFromMetricSetup(metricSetupTmp)) {
					hasCommon = false;
				}
			} else {
				metricSetup = metricSetupTmp;
			}
		});

		if (!!metricSetup && hasCommon) {
			return metricSetup.filtersData;
		}

		return null;
	}

	// ----- Tooltip stuff ----

	@action
	getTooltipText() {
		// only called when hasChanged
		if (this.navigationService.isMainApp) {
			return this.mmMetricsService.requestMetrics().then(() => {
				const dimensionItems: DSSetup.DimensionItem[] = [];
				this.metricDataItems.forEach((item) => {
					const metric = this.mmMetricsService.getMetricForKeyAndSpaceAccessId(
						item.key,
						item.spaceAccessId,
					);
					dimensionItems.push(...(metric?.dimensions || []));
				});
				return this.getTooltipTextForDimensions(dimensionItems);
			});
		} else {
			return this.mmMetricsService
				.fetchDimensions(this.metricDataItems)
				.then((dimensionItems) => {
					return this.getTooltipTextForDimensions(dimensionItems);
				});
		}
	}

	private getTooltipTextForDimensions(dimensionItems: DSSetup.DimensionItem[]) {
		let text = '';

		const getDimensionNameForDimension = (dimension: string) => {
			return dimensionItems.find((d) => d.key === dimension)?.value || dimension;
		};

		const metricSetup = this.metricSetup.createCopy();
		metricSetup.dimensions = this.dimensions || [];
		metricSetup.setFiltersFromData(this.filters || null);

		const dimensionFilterGroup = metricSetup.dimensionFilterGroup;

		const savedDimensions = this.dimensions ?? [];
		const isNone = savedDimensions.length === 0;
		const dimensionLabel = isNone
			? 'None'
			: savedDimensions.map(getDimensionNameForDimension).join(', ');

		if (dimensionLabel && (!dimensionFilterGroup || !isNone)) {
			text += `<span>Breakdown by:</span> <strong>${dimensionLabel}</strong>`;
		}

		if (dimensionFilterGroup) {
			if (text) {
				text += '<br>';
			}
			text += dimensionFilterGroup.getFilterItemsText(getDimensionNameForDimension);
		}

		return sanitize(text);
	}
	<template>
		{{#if this.hasAnyMetrics}}
			<div
				class={{joinClasses
					(array
						"master-filter"
						this.themeClass
						(if this.isPopupOpen "active")
						(if @isDisabled "disabled")
					)
				}}
			>
				<a
					class="filter-selector {{if this.hasChanges 'em'}}"
					{{on "click" this.openPopup}}
				>
					<span class="filter-label">
						<span class="filter-icon">
							<CommonUiIconComponent @name="Filter" @size={{this.iconSize}} />
							{{#if this.hasChanges}}
								<span class="badge blue"></span>

								<ToolTipComponent
									@onShowGetText={{this.getTooltipText}}
									@maxWidth={{230}}
									@extraClass="master-filter__tooltip"
								/>
							{{/if}}
						</span>

						<span class="filter-label-text">Filter data</span>
					</span>
				</a>

			</div>
		{{/if}}
	</template>
}

export default CommonMasterFilterIndexComponent;
